import { api } from "./client";
import type {
  DomainSearchResult,
  DomainHistoryItem,
  WatchlistItem
} from "../types/domainMonitor";

export interface CompanyProfile {
  domain: string;
  company_name: string;
  sector: string;
  sub_industry?: string;
  country?: string;
  description?: string;
  confidence: number;
  confidence_label: "High" | "Medium" | "Low";
  method: "cache" | "own_data" | "serper_llm" | "manual" | "user_override" | "cooldown_blocked" | "not_found" | string;
  confirmed: boolean;
  evidence: { url: string; quote: string }[];
  status?: "ready" | "not_found" | "failed" | string;
  serper_ms?: number;
  llm_ms?: number;
  total_ms?: number;
  updated_at?: string;
}

export interface SectorFacts {
  sector: string;
  period: string;
  period_label: string;
  start_date: string;
  end_date: string;
  total_posts_in_period: number;
  total_posts_in_period_formatted: string;
  sector_posts: number;
  sector_posts_formatted: string;
  ransomware_victims: number;
  ransomware_victims_formatted: string;
  share_of_all_posts: number;
  share_of_all_posts_formatted: string;
  coverage_ratio: number;
  coverage_ratio_formatted: string;
  coverage_note: string;
  top_ransomware_groups: { name: string; count: number; count_formatted: string }[];
  top_ransomware_countries: { name: string; count: number; count_formatted: string }[];
  top_darknet_sources: { name: string; count: number; count_formatted: string }[];
  top_darknet_countries: { name: string; count: number; count_formatted: string }[];
  trend_series: { date: string; count: number }[];
  method: string;
}

export interface AdminIntegrationStatus {
  serper_configured: boolean;
  serper_daily_usage: number;
  serper_daily_cap: number;
  serper_monthly_usage: number;
  serper_monthly_cap: number;
  llm_configured: boolean;
  llm_reachable: boolean;
  llm_latency_ms: number | null;
  llm_model: string;
  report_domain_policy: string;
}

export async function searchDomain(
  domain: string,
  includeSubdomains: boolean
): Promise<DomainSearchResult> {
  const res = await api.post("/domain-monitor/search", {
    domain,
    include_subdomains: includeSubdomains,
    phase: "both"
  });
  return res.data;
}

export async function searchDomainPhase(
  domain: string,
  includeSubdomains: boolean,
  phase: "direct" | "mentions" | "both" = "both",
  signal?: AbortSignal
): Promise<DomainSearchResult> {
  const res = await api.post(
    "/domain-monitor/search",
    {
      domain,
      include_subdomains: includeSubdomains,
      phase
    },
    { signal }
  );
  return res.data;
}

export async function fetchDomainCount(
  domain: string,
  includeSubdomains: boolean
): Promise<{ domain: string; total: number; is_capped: boolean; formatted: string }> {
  const res = await api.post("/domain-monitor/count", {
    domain,
    include_subdomains: includeSubdomains
  });
  return res.data;
}

export async function getDomainHistory(): Promise<DomainHistoryItem[]> {
  const res = await api.get("/domain-monitor/history");
  return res.data;
}

export async function addToWatchlist(
  domain: string,
  options?: { source?: "manual" | "client_profile" | "continuous"; label?: string; include_subdomains?: boolean }
): Promise<void> {
  await api.post("/domain-monitor/watch", {
    domain,
    source: options?.source || "manual",
    label: options?.label,
    include_subdomains: options?.include_subdomains ?? true,
  });
}

export async function getWatchlist(): Promise<WatchlistItem[]> {
  const res = await api.get("/domain-monitor/watchlist");
  return res.data;
}

export async function removeFromWatchlist(domain: string): Promise<void> {
  await api.delete(`/domain-monitor/watch/${encodeURIComponent(domain)}`);
}

export async function fetchCompanyProfile(domain: string, forceRefresh = false): Promise<CompanyProfile> {
  const res = await api.get("/report-center/profile", {
    params: { domain, force_refresh: forceRefresh }
  });
  return res.data;
}

export async function confirmCompanyProfile(domain: string, sector: string, companyName?: string): Promise<CompanyProfile> {
  const res = await api.post("/report-center/profile/confirm", {
    domain,
    sector,
    company_name: companyName
  });
  return res.data;
}

export async function fetchSectorFacts(sector: string, period = "30D"): Promise<SectorFacts> {
  const res = await api.get("/report-center/sector-facts", {
    params: { sector, period }
  });
  return res.data;
}

export async function fetchAdminIntegrationStatus(): Promise<AdminIntegrationStatus> {
  const res = await api.get("/report-center/admin/status");
  return res.data;
}

export async function toggleWatchlistStatus(
  domain: string,
  status: "active" | "paused"
): Promise<{ message: string; item: WatchlistItem }> {
  const res = await api.patch(`/domain-monitor/watch/${encodeURIComponent(domain)}`, {
    status
  });
  return res.data;
}

export async function rescanMonitoredDomain(
  domain: string
): Promise<{ message: string; item: WatchlistItem }> {
  const res = await api.post(`/domain-monitor/watch/${encodeURIComponent(domain)}/scan`);
  return res.data;
}

export async function triggerMonitoringCycle(): Promise<{ status: string; scanned_count: number; alerts_dispatched: number; timestamp: string }> {
  const res = await api.post("/domain-monitor/continuous/run-cycle");
  return res.data;
}

